import * as React from "react";
interface InputCurrencyProps {
    label?: string;
    secondaryLabel?: string;
    initial?: number | string;
    onCommit?: (newValue: string) => void;
    onChange?: (newValue: string) => void;
    commitWhenEnterPressed?: boolean;
    placeholder?: string;
    children?: React.ReactNode;
    required?: boolean;
}

// implementation loosely based on https://github.com/cchanxzy/react-currency-input-field, MIT licensed
const prefix = "$";
const allowDecimals = true;
const decimalsLimit = 2;

export const InputCurrency: React.FunctionComponent<InputCurrencyProps> = props => {
    const {
        onChange,
        initial,
        onCommit,
        label,
        secondaryLabel,
        commitWhenEnterPressed,
        children,
        required,
    } = props;
    const _defaultValue = initial ? formatValue(String(initial), prefix) : "";
    const [stateValue, setStateValue] = React.useState(_defaultValue);
    const [cursor, setCursor] = React.useState(0);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const onFocus = (): number => (stateValue ? stateValue.length : 0);

    const processChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const {
            target: { selectionStart, value },
        } = event;

        const valueOnly = cleanValue(value, allowDecimals, decimalsLimit, prefix);
        if (!valueOnly) {
            onChange && onChange(null);
            return setStateValue("");
        }

        if (checkIsValidNumber(valueOnly)) {
            const formattedValue = formatValue(valueOnly, prefix);
            if (selectionStart) {
                const cursor =
                    selectionStart + (formattedValue.length - value.length) || 1;
                setCursor(cursor);
            }
            setStateValue(formattedValue);
        }

        onChange && onChange(returnableValue(valueOnly));
    };

    React.useEffect(() => {
        if (inputRef && inputRef.current) {
            inputRef.current.setSelectionRange(cursor, cursor);
        }
    }, [cursor, inputRef, stateValue]);

    const onBlur = React.useCallback(() => {
        const valueOnly = cleanValue(stateValue, allowDecimals, decimalsLimit, prefix);
        if (onCommit && valueOnly.toString() !== initial) {
            onCommit(returnableValue(valueOnly));
        }
    }, [stateValue, onCommit, initial]);

    const onEnterPressed = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (commitWhenEnterPressed && e.key === "Enter") {
                const valueOnly = cleanValue(
                    stateValue,
                    allowDecimals,
                    decimalsLimit,
                    prefix,
                );
                onCommit(returnableValue(valueOnly));
            }
        },
        [commitWhenEnterPressed, stateValue, onCommit],
    );

    return (
        <div className="form-group">
            <div className="label-group flex-row-no-bottom-margin">
                <div className="flex-column">{label}</div>
                <div className="flex-column">{secondaryLabel}</div>
            </div>
            <div className="flex-row">
                <div className="text-input-box">
                    <input
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                        value={stateValue}
                        onBlur={onBlur}
                        onKeyDownCapture={onEnterPressed}
                        onFocus={onFocus}
                        onChange={processChange}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                        ref={inputRef as any}
                        inputMode="decimal"
                        autoComplete="off"
                        required={required}
                    />
                </div>
                {children}
            </div>
        </div>
    );
};

const addCommas = (value: string): string => value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const removeCommas = (value: string): string => value.replace(/,/g, "");

const checkIsValidNumber = (input: string): boolean => {
    if (Number(input) < 0 || isNaN(Number(input))) {
        return false;
    }

    return true;
};

/**
 * Remove prefix, commas and extra decimals from value
 */
const cleanValue = (
    value: string,
    allowDecimals: boolean,
    decimalsLimit: number,
    prefix?: string,
): string => {
    const withoutPrefix = prefix ? value.replace(prefix, "") : value;
    const withoutCommas = removeCommas(withoutPrefix);
    const parsed = withoutCommas;

    if (String(parsed).includes(".")) {
        const [int, decimals] = withoutCommas.split(".");
        const includeDecimals = allowDecimals
            ? `.${decimalsLimit ? decimals.slice(0, decimalsLimit) : decimals}`
            : "";

        return `${int}${includeDecimals}`;
    }

    return String(parsed);
};

/**
 * Format value with commas and prefix
 */
export const formatValue = (value: string, prefix?: string): string => {
    const [int, decimals] = value.split(".");
    const includePrefix = prefix ? prefix : "";
    const includeDecimals = value.includes(".") ? `.${decimals}` : "";
    return `${includePrefix}${addCommas(int)}${includeDecimals}`;
};

function returnableValue(value: string): string {
    if (!value) return "";

    const valueOnly = parseFloat(value);
    if (isNaN(valueOnly)) return "";

    if (Math.round(valueOnly) === valueOnly) {
        return valueOnly.toFixed(0);
    }
    return valueOnly.toFixed(decimalsLimit);
}
